'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import { EdgeFade } from 'designSystem/components/EdgeFade'
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useState,
} from 'react'

const OVERFLOW_THRESHOLD_PX = 1
const scrollViewportVariants = cva('min-h-0 min-w-0 flex-1', {
  variants: {
    axis: {
      vertical: 'overflow-y-auto',
      horizontal: 'overflow-x-auto',
    },
    overscroll: {
      auto: 'overscroll-auto',
      none: 'overscroll-none',
    },
    scrollbar: {
      default: '',
      hidden: 'scrollbar-hide',
      thin: '[scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar]:w-2',
    },
  },
  defaultVariants: {
    axis: 'vertical',
    scrollbar: 'default',
  },
})

const topFadeVariants = cva('', {
  variants: {
    stickyHeaderSize: {
      none: '',
      default: 'top-[calc(2.5rem-1px)]',
      compact: 'top-[calc(2rem-1px)]',
    },
  },
  defaultVariants: { stickyHeaderSize: 'none' },
})

export function ScrollFade({
  children,
  axis = 'vertical',
  fadeEdges = 'bottom',
  scrollbar = 'default',
  overscroll = 'auto',
  stickyHeaderSize = 'none',
}: {
  children: ReactNode
  fadeEdges?: 'bottom' | 'both'
} & VariantProps<typeof scrollViewportVariants> &
  VariantProps<typeof topFadeVariants>): ReactElement {
  const [scrollNode, setScrollNode] = useState<HTMLDivElement | null>(null)
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null)
  const [showTopFade, setShowTopFade] = useState(false)
  const [showBottomFade, setShowBottomFade] = useState(false)

  const updateFade = useCallback(() => {
    if (!scrollNode) return
    const horizontal = axis === 'horizontal'
    const position = horizontal ? scrollNode.scrollLeft : scrollNode.scrollTop
    const remaining = horizontal
      ? scrollNode.scrollWidth - position - scrollNode.clientWidth
      : scrollNode.scrollHeight - position - scrollNode.clientHeight
    setShowBottomFade(remaining > OVERFLOW_THRESHOLD_PX)
    setShowTopFade(position > OVERFLOW_THRESHOLD_PX)
  }, [axis, scrollNode])

  useLayoutEffect(() => {
    if (!scrollNode) return
    updateFade()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(updateFade)
    observer.observe(scrollNode)
    if (contentNode) observer.observe(contentNode)
    return () => observer.disconnect()
  }, [contentNode, scrollNode, updateFade])

  return (
    <div
      data-slot='scroll-fade'
      className='relative flex min-h-0 min-w-0 flex-1 flex-col'>
      {fadeEdges === 'both' && showTopFade ? (
        <EdgeFade
          placement='top'
          className={
            axis === 'horizontal'
              ? 'inset-x-auto inset-y-0 left-0 h-auto w-6'
              : topFadeVariants({ stickyHeaderSize })
          }
          style={
            axis === 'horizontal'
              ? {
                  background:
                    'linear-gradient(to right, var(--edge-fade-from, var(--card)), transparent)',
                }
              : undefined
          }
        />
      ) : null}
      <div
        ref={setScrollNode}
        className={scrollViewportVariants({ axis, scrollbar, overscroll })}
        tabIndex={axis === 'horizontal' ? 0 : undefined}
        onScroll={updateFade}>
        <div
          ref={setContentNode}
          className={axis === 'horizontal' ? 'w-max min-w-full' : 'min-w-0'}>
          {children}
        </div>
      </div>
      {showBottomFade ? (
        <EdgeFade
          className={
            axis === 'horizontal'
              ? 'inset-x-auto inset-y-0 right-0 h-auto w-6'
              : undefined
          }
          style={
            axis === 'horizontal'
              ? {
                  background:
                    'linear-gradient(to left, var(--edge-fade-from, var(--card)), transparent)',
                }
              : undefined
          }
        />
      ) : null}
    </div>
  )
}
